/**
 * HomePage.js — Store home page.
 *
 * Fetches home-page content from the admin panel API (banner, advantages, featured programs,
 * practice areas) and product data from the engine API, then renders the page sections.
 *
 * Props:
 *  - useLoggedIn {boolean}             Whether the current user is authenticated.
 *  - UserData {object|undefined}       Logged-in user details (includes email).
 *  - AdminPanelURL {string}            Base URL for the admin panel API.
 *  - EngineURL {string}                Base URL for the engine API.
 *  - Program_structure_images {Array}  Slug-to-image-URL mapping.
 *  - AddtoCart {Function}              Adds a product variant to the cart.
 *  - PopularTag {Array}                Popular search tags for the SearchForm.
 *  - offerBarTwo {boolean}             Controls the second offer bar visibility.
 *  - setofferBarTwo {Function}         Setter for offerBarTwo.
 *  - lastURL {string|undefined}        Last visited URL (for back-navigation tracking).
 *  - setlastURL {Function}             Setter for lastURL.
 *  - ShowLoginPopup {Function}         Opens the login modal.
 *  - ShowSignUpPopup {Function}        Opens the sign-up modal.
 *  - ProductPageJson {Array}           Store page menu data.
 *  - prodcut_list_order {boolean}      Controls product list order direction.
 *  - set_prodcut_list_order {Function} Setter for prodcut_list_order.
 *  - UpdateCartQty {Function}          Updates the cart badge count.
 *  - UpdateCartAmount {Function}       Updates the cart total display.
 */

// ─── External libraries ───────────────────────────────────────────────────────
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import queryString from 'query-string';
import axios from 'axios';
import { Helmet } from 'react-helmet';
import { Container } from 'react-bootstrap';
import { safeDate } from '../../utils/date';

// ─── Internal — section components ───────────────────────────────────────────
import Banner from '../Banner/Banner';
import AdvantageSection from '../Sections/Advantage';
import SearchForm from '../Sections/SearchForm';
import FeaturedProduct from '../Sections/FeaturedProduct';
import AreasSection from '../Sections/AreasSection';

// ─── Internal — icons ─────────────────────────────────────────────────────────
import { LoaderIcon } from "../../icons";

// ─── Main component ───────────────────────────────────────────────────────────

/**
 * HomePage — store home page.
 * See file-level JSDoc for full props description.
 */
export const HomePage = (props) => {

  // ── Banner content state ──────────────────────────────────────────────────
  const [BannerTitle, setBannerTitle] = useState();
  const [BannerSubTitle, setBannerSubTitle] = useState();
  const [BannerImage, setBannerImage] = useState();
  const [BannerButtonText, setBannerButtonText] = useState();
  const [BannerButtonUrl, setBannerButtonUrl] = useState();

  // ── Advantage section state ───────────────────────────────────────────────
  const [AdvantageTitle, setAdvantageTitle] = useState();
  const [AdvantageList, setAdvantageList] = useState([]);

  // ── Featured / practice area state ───────────────────────────────────────
  const [featuredProgramList, setfeaturedProgramList] = useState([]);
  const [selectedPracticeArea, setselectedPracticeArea] = useState([]);

  // ── Router helpers ────────────────────────────────────────────────────────
  const location = useLocation();
  const queryParam = queryString.parse(location.search);

  // Preserve Google Ads click ID across navigation.
  const GAurl = queryParam['gclid'] !== undefined ? '?gclid=' + queryParam['gclid'] : '';

  const [userData_, setUserData_] = useState(undefined);

  const getProductData = (product) => {
    try {
      // Handle if product is an array with product at index 1
      const productObj = Array.isArray(product) ? product[1] : product;

      if (!productObj || typeof productObj !== "object") {
        return null;
      }

      return productObj;
    } catch (error) {
      console.error("Error extracting product data:", error);
      return null;
    }
  };

  /**
   * Fetches home-page banner/advantage/featured data from the admin panel API,
   * and then fetches all product data from the engine API.
   *
   * @param {string}         tags      - Tag filter string for the admin panel request.
   * @param {string|undefined} useemaill - Logged-in user's email, or undefined for guests.
   */
  const fetchData = useCallback(async (tags, useemaill) => {
    setLoading(true);

    try {
      const admin_panel_api_URL = props.useLoggedIn
        ? props.AdminPanelURL + 'api/home_page_details?email=' + useemaill
        : props.AdminPanelURL + 'api/home_page_details';

      const admin_panel_response = await axios({
        method: 'get',
        url: admin_panel_api_URL,
        headers: {
            'X-API-KEY': props.API_KEY   // ← Add this
        }
      });

      if (admin_panel_response.status === 200) {
        let data = admin_panel_response.data;
        if (data.length !== 0) {
          setBannerTitle(data.banner.banner_title);
          setBannerSubTitle(data.banner.banner_subtitle);
          setBannerImage(data.banner.banner_image);
          setBannerButtonText(data.banner.banner_btn_text);
          setBannerButtonUrl(data.banner.banner_btn_url);
          setAdvantageTitle(data.advantages.title);
          setAdvantageList(data.advantages.tabs);
          setfeaturedProgramList(data.featured_programs);
          if (props.useLoggedIn) {
            setselectedPracticeArea(data.practice_areas);
          } else {
            // For guests, restore practice area selection from localStorage.
            if (localStorage.getItem("new_practice_area") !== null) {
              setselectedPracticeArea(localStorage.getItem("new_practice_area").split(","));
            }
          }
        }
      }

      // Fetch the full product catalogue from the engine.
      const api_URL = props.EngineURL + 'shopify-pings/getdata.php';
      const response = await axios(api_URL);

      if (response.status === 200) {
        let data = response.data;
        if (data.length !== 0) {
          // Convert the keyed object to an array of [index, product] pairs.
          var result = Object.keys(data).map((key) => [Number(key), data[key]]);

          let cpd_programs = [];
          result.forEach(function (val) {
            const product = getProductData(val);
            if (!product) return;
            const prod_tags = product.tags || [];
            if (prod_tags.includes('cpd-programs-menu')) {
              cpd_programs.push(val);
            }
          });

          setUserData_({ products: result, cpd_programs: cpd_programs });
        } else {
          setUserData_({ products: [] });
        }
        setTimeout(function(){
          setLoading(false);
        },500);        
      }
    } catch (error) {
    }
    setLoading(false);
  }, [props.useLoggedIn, props.AdminPanelURL, props.EngineURL]);


  /**
   * Re-fetches home page data when the logged-in user changes.
   * Passes the user's email so the API can personalise the response.
   */
  useEffect(() => {
    if (props.useLoggedIn) {
      if (props.UserData !== undefined) {
        fetchData('cpd-programs-menu', props.UserData.email);
      }
    } else {
      fetchData('cpd-programs-menu', undefined);
    }
  }, [props.UserData,fetchData,props.useLoggedIn]);

  // ── Loading / error state ─────────────────────────────────────────────────
  const [isLoading, setLoading] = useState(true);

  

  useEffect(() => {
    // TODO: consider adding a data-refetch trigger here if needed in future.
  }, []);


  /**
   * Filters and sorts a product array based on featured program titles, past-date exclusion,
   * and hide-from-list flag.
   *
   * @param {Array}          data                - Full product list as [index, product] pairs.
   * @param {Array|undefined} featuredProgramList_ - Titles to include (undefined = all).
   * @param {*}              selectedProgram_    - Unused — kept for API compatibility.
   * @returns {Array} Filtered and date-sorted product list.
   */
  function mysearch(data, featuredProgramList_, selectedProgram_) {
    const filterRespones = (data || []).filter(function (pppp) {
      let ppp = Object.values(pppp);

      var search_text = false;
      var hidefromlist = false;
      var datapassed = true;
      var defaaultt = false;

      var CurrentDate = new Date();

      if (featuredProgramList_ !== undefined || selectedProgram_ !== undefined) {
        (ppp[1]?.metafields || []).forEach(function (metadata) {
          if (metadata.key === "all_data") {
            var prodcutJson = JSON.parse(metadata.value);

            // Exclude products whose start date has passed.
            var is_program_gone = program_date_is_gone_or_not(prodcutJson.start_date_time);
            if (prodcutJson.start_date_time !== null && is_program_gone) {
              datapassed = false;
            }

            // Respect the engine's hide-from-list flag.
            if (prodcutJson.Hide_from_list !== true) {
              hidefromlist = true;
            }

            // Only include products whose title is in the featured list (if provided).
            if (featuredProgramList_ !== null && featuredProgramList_ !== undefined) {
              if (featuredProgramList_.includes(ppp[1].title)) {
                search_text = true;
              }
            } else {
              search_text = true;
            }

            if (datapassed && hidefromlist && search_text) {
              defaaultt = true;
            }
          }
        });
      } else {
        (ppp[1]?.metafields || []).forEach(function (metadata) {
          if (metadata.key === "all_data") {
            var prodcutJson = JSON.parse(metadata.value);
            if (prodcutJson.Hide_from_list !== true) {
              hidefromlist = true;
            }

            var past = safeDate(prodcutJson.end_date);
            if (prodcutJson.end_date !== undefined && dateInPast(past, CurrentDate)) {
              datapassed = true;
            }
          }
        });

        if (datapassed && hidefromlist) {
          defaaultt = true;
        }
      }

      return defaaultt;
    });

    // Sort by start_date_time ascending; products without a date (e.g. downloadable) go last.
    filterRespones.sort(function (aa, bb) {
      let a = Object.values(aa);
      let b = Object.values(bb);
      var pdate1 = a[1];
      var pdate2 = b[1];

      var date1 = null;
      var date2 = null;

      (a[1]?.metafields || []).forEach(function (metadata) {
        if (metadata.key === "all_data") {
          var prodcutJson1 = JSON.parse(metadata.value);
          date1 = prodcutJson1.start_date_time;
        }
      });

      (b[1]?.metafields || []).forEach(function (metadata) {
        if (metadata.key === "all_data") {
          var prodcutJson2 = JSON.parse(metadata.value);
          date2 = prodcutJson2.start_date_time;
        }
      });

      // Push undated or downloadable products to the end of the list.
      if (date1 === null || date1 === "" || pdate1.tags.includes("online-intensive-(to-download)")) {
        return 1;
      } else if (date2 === null || date2 === "" || pdate2.tags.includes("online-intensive-(to-download)")) {
        return -1;
      } else {
        return safeDate(date1) - safeDate(date2);
      }
    });

    return filterRespones;
  }

  /**
   * Returns true if firstDate is on or before secondDate (ignoring time).
   *
   * @param {Date} firstDate
   * @param {Date} secondDate
   * @returns {boolean}
   */
  function dateInPast(firstDate, secondDate) {
    if (firstDate.setHours(0, 0, 0, 0) <= secondDate.setHours(0, 0, 0, 0)) {
      return true;
    }
    return false;
  }

  /**
   * Returns true if the program starts within the next 10 minutes.
   *
   * @param {string|null|undefined} program_start_date_time - ISO date string.
   * @returns {boolean}
   */
  const is_ten_minite_less = (program_start_date_time) => {
    if (program_start_date_time === undefined || program_start_date_time === null || program_start_date_time === "") {
      return false;
    }
    program_start_date_time = safeDate(program_start_date_time);
    let program_time_modify = program_start_date_time.setHours(program_start_date_time.getHours());
    let current_time_10 = new Date(new Date().getTime() + 1000 * 600);
    let current_time = new Date().getTime();

    var from = current_time;
    var to = current_time_10;
    var check = program_time_modify;

    return from <= check && check <= to;
  };

  /**
   * Returns true if the program start date/time is in the past.
   *
   * @param {string|null} program_start_date_time - ISO date string or null.
   * @returns {boolean}
   */
  const program_date_is_gone_or_not = (program_start_date_time) => {
    if (program_start_date_time === null) {
      return false;
    }
    program_start_date_time = safeDate(program_start_date_time);
    let program_time_modify = program_start_date_time.setHours(program_start_date_time.getHours());
    let current_time = new Date().getTime();
    if ((parseFloat(program_time_modify) - parseFloat(current_time)) > 0) {
      return false;
    } else {
      return true;
    }
  };

  // Show a full-page loader until the product data has loaded.
  if (BannerTitle === '') {
    return <><div className="pre_loader l1"><LoaderIcon /></div></>;
  }

  return (
    <>
      <Helmet>
        <title>Leo Cussen Centre for Law</title>
        <link rel="canonical" href={"https://store.leocussen.edu.au/"} />
        <meta name="description" content="" />
      </Helmet>

      <Banner
        BannerTitle={BannerTitle}
        BannerSubTitle={BannerSubTitle}
        BannerImage={BannerImage}
        BannerButtonText={BannerButtonText}
        BannerButtonUrl={BannerButtonUrl}
        GAurl={GAurl}
      />

      <AdvantageSection
        AdvantageTitle={AdvantageTitle}
        AdvantageList={AdvantageList}
        GAurl={GAurl}
      />

      <SearchForm
        PopularTag={props.PopularTag}
        GAurl={GAurl}
      />

      {userData_ !== undefined ? (
        <FeaturedProduct
          GAurl={GAurl}
          program_date_is_gone_or_not={program_date_is_gone_or_not}
          is_ten_minite_less={is_ten_minite_less}
          Program_structure_images={props.Program_structure_images}
          isLoading={isLoading}
          productData={mysearch(userData_.products, featuredProgramList, undefined)}
          AddtoCart={props.AddtoCart}
          AddtoWishlist={props.AddtoWishlist}
          getAllWishlist={props.getAllWishlist}
        />
      ) : (
        <Container style={{ height: "400px" }}>
          <div className='title_wrap text-center'>
            <h2 className='text-primary'>Featured Programs loading...</h2>
          </div>
        </Container>
      )}

      {userData_ !== undefined ? (
        <AreasSection
          selectedProgram={selectedPracticeArea}
          program_date_is_gone_or_not={program_date_is_gone_or_not}
          is_ten_minite_less={is_ten_minite_less}
          Program_structure_images={props.Program_structure_images}
          products={mysearch(userData_.cpd_programs)}
          AddtoCart={props.AddtoCart}
          GAurl={GAurl}
          AddtoWishlist={props.AddtoWishlist}
          getAllWishlist={props.getAllWishlist}
        />
      ) : ''}
    </>
  );
};
